import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  FileDown,
  FileSpreadsheet,
  LayoutTemplate,
  Pencil,
  Plus,
  Search,
  Settings2,
  Sparkles,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { readToken, tenantTokenKey } from "../../app/session";
import { CroatianDateInput } from "../../components/CroatianDateInput";
import {
  MobileVirtualList,
  useMobileViewport,
} from "../../components/MobileVirtualList";
import { useTranslation } from "../../i18n/i18n";
import { ApiError, apiDownload, apiRequest } from "../../lib/api";
import { useTenantApp } from "../app/TenantAppContext";
import {
  manualShiftEntryProblem,
  resolveManualShiftTypeId,
} from "./shift-entry";

interface Shift {
  id: string;
  userId: string;
  userName: string;
  shiftTypeId: string;
  code: string;
  typeName: string;
  color: string;
  startsAt: string | null;
  endsAt: string | null;
  breakMinutes: number;
  isWorking: boolean;
  shiftDate: string;
  note: string | null;
}

interface Holiday {
  id?: string;
  date: string;
  name: string;
  source: "STATUTORY" | "COMPANY";
}

interface ShiftType {
  id: string;
  code: string;
  name: string;
  color: string;
  startsAt: string | null;
  endsAt: string | null;
  breakMinutes: number;
  isWorking: boolean;
  status: "ACTIVE" | "INACTIVE";
}

interface ShiftTemplate {
  id: string;
  name: string;
  description: string | null;
  cycleWeeks: number;
}

interface Options {
  types: ShiftType[];
  users: Array<{ id: string; firstName: string; lastName: string }>;
  teams: Array<{ id: string; name: string }>;
  templates: ShiftTemplate[];
}

interface AvailabilityUser {
  id: string;
  name: string;
  conflictDates: string[];
  sourceCount?: number;
}

interface AvailabilityPreview {
  targetFrom: string;
  targetTo: string;
  users: AvailabilityUser[];
  scheduledDays?: number;
  template?: { id: string; name: string; cycleWeeks: number };
}

const dayNames = ["Ponedjeljak", "Utorak", "Srijeda", "Četvrtak", "Petak", "Subota", "Nedjelja"];

function isoDate(date: Date): string {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().slice(0, 10);
}

function mondayOf(date: Date): Date {
  const copy = new Date(date);
  const day = copy.getDay() || 7;
  copy.setDate(copy.getDate() - day + 1);
  copy.setHours(12, 0, 0, 0);
  return copy;
}

function mondayIso(value: string): string {
  if (!value) return "";
  return isoDate(mondayOf(new Date(`${value}T12:00:00`)));
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function formatDate(value: string): string {
  return new Date(`${value}T12:00:00`).toLocaleDateString("hr-HR");
}

function formatTime(value: string | null): string {
  return value ? value.slice(0, 5) : "—";
}

function emptyTemplateEntries() {
  return Array.from({ length: 42 }, (_, dayOffset) => ({
    dayOffset,
    shiftTypeId: "",
  }));
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function AvailabilityPicker(props: {
  users: AvailabilityUser[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  emptyText: string;
}) {
  const [search, setSearch] = useState("");
  const filtered = props.users.filter((user) =>
    user.name.toLocaleLowerCase("hr-HR").includes(search.toLocaleLowerCase("hr-HR").trim()),
  );
  const available = filtered.filter(
    (user) => user.conflictDates.length === 0 && user.sourceCount !== 0,
  );

  function toggle(user: AvailabilityUser) {
    if (user.conflictDates.length || user.sourceCount === 0) return;
    props.onChange(
      props.selectedIds.includes(user.id)
        ? props.selectedIds.filter((id) => id !== user.id)
        : [...props.selectedIds, user.id],
    );
  }

  return (
    <section className="shift-employee-picker">
      <header>
        <div>
          <strong>Odaberite zaposlenike</strong>
          <small>
            Zaposlenici s postojećom smjenom u ciljnom razdoblju automatski su
            zaštićeni i ne mogu se odabrati.
          </small>
        </div>
        <b>{props.selectedIds.length} odabrano</b>
      </header>
      <div className="shift-employee-picker__toolbar">
        <label className="compact-search">
          <Search />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Pronađi zaposlenika…"
          />
        </label>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() =>
            props.onChange([
              ...new Set([...props.selectedIds, ...available.map((user) => user.id)]),
            ])
          }
          disabled={!available.length}
        >
          <Check /> Odaberi dostupne
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => props.onChange([])}
          disabled={!props.selectedIds.length}
        >
          Očisti
        </button>
      </div>
      <div className="shift-employee-picker__list">
        {filtered.map((user) => {
          const disabled = user.conflictDates.length > 0 || user.sourceCount === 0;
          return (
            <label
              key={user.id}
              className={`${props.selectedIds.includes(user.id) ? "is-selected" : ""} ${disabled ? "is-disabled" : ""}`}
            >
              <input
                type="checkbox"
                checked={props.selectedIds.includes(user.id)}
                disabled={disabled}
                onChange={() => toggle(user)}
              />
              <span className="avatar avatar--soft">
                {user.name
                  .split(" ")
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span>
                <strong>{user.name}</strong>
                <small>
                  {user.conflictDates.length
                    ? `Već ima smjenu: ${user.conflictDates.map(formatDate).join(", ")}`
                    : user.sourceCount === 0
                      ? "Nema smjena u izvornom tjednu"
                      : "Dostupan za primjenu"}
                </small>
              </span>
              {disabled ? <Clock3 /> : <Check />}
            </label>
          );
        })}
        {!filtered.length && <div className="table-empty">{props.emptyText}</div>}
      </div>
    </section>
  );
}

export function ShiftsPage() {
  const { context } = useTenantApp();
  const { t } = useTranslation();
  const canManage = context.user.permissions.includes("shifts.manage");
  const canManageHolidays = canManage && context.user.dataScope === "COMPANY";
  const isMobile = useMobileViewport();
  const [week, setWeek] = useState(() => mondayOf(new Date()));
  const [mobileDate, setMobileDate] = useState(() => isoDate(new Date()));
  const [items, setItems] = useState<Shift[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [options, setOptions] = useState<Options>({
    types: [],
    users: [],
    teams: [],
    templates: [],
  });
  const [teamId, setTeamId] = useState("");
  const [userId, setUserId] = useState("");
  const [edit, setEdit] = useState<{
    userId: string;
    date: string;
    shift?: Shift;
  } | null>(null);
  const [typeId, setTypeId] = useState("");
  const [note, setNote] = useState("");
  const [typesModal, setTypesModal] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [typeForm, setTypeForm] = useState({
    code: "",
    name: "",
    color: "#32B8C6",
    startsAt: "06:00",
    endsAt: "14:00",
    breakMinutes: 30,
    isWorking: true,
    status: "ACTIVE" as "ACTIVE" | "INACTIVE",
  });
  const [templateModal, setTemplateModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    cycleWeeks: 1,
    entries: emptyTemplateEntries(),
  });
  const [applyModal, setApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({
    templateId: "",
    startsOn: isoDate(mondayOf(new Date())),
    cycles: 1,
    userIds: [] as string[],
  });
  const [applyPreview, setApplyPreview] = useState<AvailabilityPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [copyModal, setCopyModal] = useState(false);
  const [copyForm, setCopyForm] = useState({
    targetFrom: isoDate(addDays(mondayOf(new Date()), 7)),
    userIds: [] as string[],
  });
  const [copyPreview, setCopyPreview] = useState<AvailabilityPreview | null>(null);
  const [holidaysModal, setHolidaysModal] = useState(false);
  const [holidayYear, setHolidayYear] = useState(week.getFullYear());
  const [yearHolidays, setYearHolidays] = useState<Holiday[]>([]);
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [manualError, setManualError] = useState("");
  const [notice, setNotice] = useState("");
  const dates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(week, index)),
    [week],
  );
  const activeTypes = useMemo(
    () => options.types.filter((type) => type.status === "ACTIVE"),
    [options.types],
  );

  useEffect(() => {
    if (!dates.some((date) => isoDate(date) === mobileDate)) {
      setMobileDate(isoDate(dates[0]!));
    }
  }, [dates, mobileDate]);

  const token = () => readToken(tenantTokenKey);

  async function load() {
    setError("");
    try {
      const query = new URLSearchParams({
        from: isoDate(week),
        to: isoDate(addDays(week, 6)),
      });
      if (teamId) query.set("teamId", teamId);
      if (userId) query.set("userId", userId);
      const [shifts, opts] = await Promise.all([
        apiRequest<{ items: Shift[]; holidays: Holiday[] }>(
          `/api/v1/shifts?${query}`,
          { accessToken: token() },
        ),
        apiRequest<Options>("/api/v1/shifts/options", { accessToken: token() }),
      ]);
      setItems(shifts.items);
      setHolidays(shifts.holidays);
      setOptions(opts);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Smjene nisu dostupne.");
    }
  }

  useEffect(() => {
    void load();
  }, [week, teamId, userId]);

  useEffect(() => {
    if (!applyModal || !applyForm.templateId) {
      setApplyPreview(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      void apiRequest<AvailabilityPreview>(
        `/api/v1/shifts/templates/${applyForm.templateId}/preview`,
        {
          method: "POST",
          accessToken: token(),
          body: JSON.stringify({
            startsOn: applyForm.startsOn,
            cycles: applyForm.cycles,
            ...(teamId ? { teamId } : {}),
          }),
        },
      )
        .then((preview) => {
          setApplyPreview(preview);
          const availableIds = new Set(
            preview.users
              .filter((user) => !user.conflictDates.length)
              .map((user) => user.id),
          );
          setApplyForm((current) => ({
            ...current,
            userIds: current.userIds.filter((id) => availableIds.has(id)),
          }));
        })
        .catch((caught) =>
          setError(caught instanceof ApiError ? caught.message : "Provjeru rasporeda nije moguće učitati."),
        )
        .finally(() => setPreviewLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [applyModal, applyForm.templateId, applyForm.startsOn, applyForm.cycles, teamId]);

  useEffect(() => {
    if (!copyModal) {
      setCopyPreview(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      void apiRequest<AvailabilityPreview>("/api/v1/shifts/copy/preview", {
        method: "POST",
        accessToken: token(),
        body: JSON.stringify({
          sourceFrom: isoDate(week),
          targetFrom: copyForm.targetFrom,
          ...(teamId ? { teamId } : {}),
        }),
      })
        .then((preview) => {
          setCopyPreview(preview);
          const availableIds = new Set(
            preview.users
              .filter((user) => !user.conflictDates.length && user.sourceCount !== 0)
              .map((user) => user.id),
          );
          setCopyForm((current) => ({
            ...current,
            userIds: current.userIds.filter((id) => availableIds.has(id)),
          }));
        })
        .catch((caught) =>
          setError(caught instanceof ApiError ? caught.message : "Provjeru kopiranja nije moguće učitati."),
        )
        .finally(() => setPreviewLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [copyModal, copyForm.targetFrom, week, teamId]);

  const users = useMemo(() => {
    const ids = new Set(items.map((item) => item.userId));
    if (context.user.dataScope === "SELF") {
      return options.users.filter((user) => user.id === context.user.id);
    }
    return options.users.filter((user) => !userId || user.id === userId || ids.has(user.id));
  }, [options.users, items, userId, context.user]);

  function holidayFor(date: Date): Holiday | undefined {
    return holidays.find((holiday) => holiday.date === isoDate(date));
  }

  function openCell(uid: string, date: string) {
    const problem = manualShiftEntryProblem(canManage, activeTypes.length);
    if (problem) {
      setError(problem);
      return;
    }
    const shift = items.find(
      (item) => item.userId === uid && item.shiftDate.slice(0, 10) === date,
    );
    setError("");
    setManualError("");
    setEdit({ userId: uid, date, shift });
    setTypeId(resolveManualShiftTypeId(shift?.shiftTypeId, options.types));
    setNote(shift?.note ?? "");
  }

  async function saveShift(event: FormEvent) {
    event.preventDefault();
    if (!edit) return;
    if (!typeId) {
      setManualError("Odaberite aktivnu vrstu smjene.");
      return;
    }
    setManualError("");
    setBusy(true);
    try {
      await apiRequest("/api/v1/shifts/batch", {
        method: "POST",
        accessToken: token(),
        body: JSON.stringify({
          shifts: [{ userId: edit.userId, shiftTypeId: typeId, shiftDate: edit.date, note }],
        }),
      });
      setEdit(null);
      setNotice("Smjena je spremljena.");
      await load();
    } catch (caught) {
      setManualError(
        caught instanceof ApiError ? caught.message : "Smjenu nije moguće spremiti.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function deleteShift() {
    if (!edit?.shift) return;
    setManualError("");
    setBusy(true);
    try {
      await apiRequest(`/api/v1/shifts/${edit.shift.id}`, {
        method: "DELETE",
        accessToken: token(),
      });
      setEdit(null);
      setNotice("Smjena je obrisana.");
      await load();
    } catch (caught) {
      setManualError(
        caught instanceof ApiError ? caught.message : "Smjenu nije moguće obrisati.",
      );
    } finally {
      setBusy(false);
    }
  }

  function resetTypeForm() {
    setEditingTypeId(null);
    setTypeForm({
      code: "",
      name: "",
      color: "#32B8C6",
      startsAt: "06:00",
      endsAt: "14:00",
      breakMinutes: 30,
      isWorking: true,
      status: "ACTIVE",
    });
  }

  function editType(type: ShiftType) {
    setEditingTypeId(type.id);
    setTypeForm({
      code: type.code,
      name: type.name,
      color: type.color,
      startsAt: type.startsAt?.slice(0, 5) ?? "",
      endsAt: type.endsAt?.slice(0, 5) ?? "",
      breakMinutes: type.breakMinutes,
      isWorking: type.isWorking,
      status: type.status,
    });
  }

  async function saveType(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await apiRequest(
        editingTypeId ? `/api/v1/shifts/types/${editingTypeId}` : "/api/v1/shifts/types",
        {
          method: editingTypeId ? "PATCH" : "POST",
          accessToken: token(),
          body: JSON.stringify(typeForm),
        },
      );
      resetTypeForm();
      await load();
      setNotice("Šifrarnik smjena je spremljen.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Vrstu smjene nije moguće spremiti.");
    } finally {
      setBusy(false);
    }
  }

  async function archiveType(type: ShiftType) {
    if (!confirm(`Arhivirati vrstu smjene „${type.name}”?`)) return;
    try {
      await apiRequest(`/api/v1/shifts/types/${type.id}`, {
        method: "DELETE",
        accessToken: token(),
      });
      if (editingTypeId === type.id) resetTypeForm();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Vrstu smjene nije moguće arhivirati.");
    }
  }

  function resetTemplateForm() {
    setEditingTemplateId(null);
    setTemplateForm({
      name: "",
      description: "",
      cycleWeeks: 1,
      entries: emptyTemplateEntries(),
    });
  }

  async function editTemplate(templateId: string) {
    const template = await apiRequest<{
      id: string;
      name: string;
      description: string | null;
      cycleWeeks: number;
      entries: Array<{ dayOffset: number; shiftTypeId: string }>;
    }>(`/api/v1/shifts/templates/${templateId}`, { accessToken: token() });
    const entries = emptyTemplateEntries();
    template.entries.forEach((entry) => {
      entries[entry.dayOffset] = {
        dayOffset: entry.dayOffset,
        shiftTypeId: entry.shiftTypeId,
      };
    });
    setEditingTemplateId(template.id);
    setTemplateForm({
      name: template.name,
      description: template.description ?? "",
      cycleWeeks: template.cycleWeeks,
      entries,
    });
  }

  async function saveTemplate(event: FormEvent) {
    event.preventDefault();
    const entries = templateForm.entries
      .slice(0, templateForm.cycleWeeks * 7)
      .filter((entry) => entry.shiftTypeId);
    if (!entries.length) {
      setError("U predlošku odaberite najmanje jednu smjenu.");
      return;
    }
    setBusy(true);
    try {
      await apiRequest(
        editingTemplateId
          ? `/api/v1/shifts/templates/${editingTemplateId}`
          : "/api/v1/shifts/templates",
        {
          method: editingTemplateId ? "PATCH" : "POST",
          accessToken: token(),
          body: JSON.stringify({ ...templateForm, entries }),
        },
      );
      resetTemplateForm();
      await load();
      setNotice("Tjedni predložak je spremljen.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Predložak nije moguće spremiti.");
    } finally {
      setBusy(false);
    }
  }

  async function archiveTemplate(template: ShiftTemplate) {
    if (!confirm(`Arhivirati predložak „${template.name}”?`)) return;
    await apiRequest(`/api/v1/shifts/templates/${template.id}`, {
      method: "DELETE",
      accessToken: token(),
    });
    if (editingTemplateId === template.id) resetTemplateForm();
    await load();
  }

  function openApply() {
    setApplyForm({
      templateId: options.templates[0]?.id ?? "",
      startsOn: isoDate(week),
      cycles: 1,
      userIds: [],
    });
    setApplyPreview(null);
    setError("");
    setApplyModal(true);
  }

  async function applyTemplate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await apiRequest<{ saved: number; skipped: number }>(
        `/api/v1/shifts/templates/${applyForm.templateId}/apply`,
        {
          method: "POST",
          accessToken: token(),
          body: JSON.stringify({
            ...applyForm,
            ...(teamId ? { teamId } : {}),
          }),
        },
      );
      setApplyModal(false);
      setNotice(
        `Predložak je primijenjen: ${result.saved} smjena spremljeno${result.skipped ? `, ${result.skipped} postojećih zapisa sigurno preskočeno` : ""}.`,
      );
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Predložak nije moguće primijeniti.");
    } finally {
      setBusy(false);
    }
  }

  function openCopy() {
    setCopyForm({ targetFrom: isoDate(addDays(week, 7)), userIds: [] });
    setCopyPreview(null);
    setError("");
    setCopyModal(true);
  }

  async function copyWeek(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await apiRequest<{ copied: number; skipped: number }>(
        "/api/v1/shifts/copy",
        {
          method: "POST",
          accessToken: token(),
          body: JSON.stringify({
            sourceFrom: isoDate(week),
            targetFrom: copyForm.targetFrom,
            userIds: copyForm.userIds,
            ...(teamId ? { teamId } : {}),
          }),
        },
      );
      setCopyModal(false);
      setNotice(
        `Tjedan je kopiran: ${result.copied} smjena spremljeno${result.skipped ? `, ${result.skipped} postojećih zapisa preskočeno` : ""}.`,
      );
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Tjedan nije moguće kopirati.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadSchedule(kind: "xlsx" | "pdf") {
    setBusy(true);
    try {
      const query = new URLSearchParams({
        from: isoDate(week),
        to: isoDate(addDays(week, 6)),
      });
      if (teamId) query.set("teamId", teamId);
      if (userId) query.set("userId", userId);
      const result = await apiDownload(`/api/v1/shifts/export.${kind}?${query}`, token());
      triggerDownload(result.blob, result.filename);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Izvoz rasporeda nije uspio.");
    } finally {
      setBusy(false);
    }
  }

  async function openHolidays() {
    setHolidaysModal(true);
    setError("");
    const response = await apiRequest<{ items: Holiday[] }>(
      `/api/v1/shifts/holidays?year=${holidayYear}`,
      { accessToken: token() },
    );
    setYearHolidays(response.items);
  }

  async function loadHolidayYear(nextYear: number) {
    setHolidayYear(nextYear);
    const response = await apiRequest<{ items: Holiday[] }>(
      `/api/v1/shifts/holidays?year=${nextYear}`,
      { accessToken: token() },
    );
    setYearHolidays(response.items);
  }

  async function addHoliday(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await apiRequest("/api/v1/shifts/holidays", {
        method: "POST",
        accessToken: token(),
        body: JSON.stringify(holidayForm),
      });
      setHolidayForm({ date: "", name: "" });
      await loadHolidayYear(holidayYear);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Neradni dan nije moguće dodati.");
    } finally {
      setBusy(false);
    }
  }

  async function removeHoliday(id: string) {
    if (!confirm("Obrisati interni neradni dan?")) return;
    await apiRequest(`/api/v1/shifts/holidays/${id}`, {
      method: "DELETE",
      accessToken: token(),
    });
    await loadHolidayYear(holidayYear);
    await load();
  }

  return (
    <div className="module-page shifts-page">
      <header className="module-page__hero">
        <div>
          <span className="eyebrow">PLANIRANJE · TIM</span>
          <h1>{t("shifts.title")}</h1>
          <p>
            Tjedni raspored, predlošci, vlastite vrste smjena i neradni dani u
            jednom preglednom planu.
          </p>
        </div>
        <div className="header-actions">
          {canManageHolidays && (
            <button className="button button--ghost" onClick={() => void openHolidays()}>
              <CalendarDays /> Praznici
            </button>
          )}
          {canManage && (
            <>
              <button
                className="button button--ghost"
                onClick={() => {
                  resetTypeForm();
                  setTypesModal(true);
                }}
              >
                <Settings2 /> Šifrarnik smjena
              </button>
              <button className="button button--ghost" onClick={openCopy}>
                <Copy /> Kopiraj tjedan
              </button>
              <button
                className="button button--ghost"
                onClick={() => {
                  resetTemplateForm();
                  setTemplateModal(true);
                }}
              >
                <LayoutTemplate /> Predlošci
              </button>
              <button
                className="button button--primary"
                data-mobile-fab
                aria-label="Primijeni predložak smjena"
                onClick={openApply}
              >
                <Plus /> Primijeni predložak
              </button>
            </>
          )}
        </div>
      </header>

      {notice && (
        <button className="task-notice" onClick={() => setNotice("")}>
          <Sparkles />
          <span>{notice}</span>
          <X />
        </button>
      )}

      <section className="shift-toolbar data-card">
        <div className="week-pager">
          <button className="icon-button" onClick={() => setWeek(addDays(week, -7))}>
            <ChevronLeft />
          </button>
          <strong>
            {dates[0]?.toLocaleDateString("hr-HR", { day: "2-digit", month: "long" })} –{" "}
            {dates[6]?.toLocaleDateString("hr-HR", { day: "2-digit", month: "long", year: "numeric" })}
          </strong>
          <button className="icon-button" onClick={() => setWeek(addDays(week, 7))}>
            <ChevronRight />
          </button>
        </div>
        <div className="header-actions">
          <select value={teamId} onChange={(event) => setTeamId(event.target.value)}>
            <option value="">Svi timovi</option>
            {options.teams.map((team) => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
          <select value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">Svi zaposlenici</option>
            {options.users.map((user) => (
              <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
            ))}
          </select>
          <button className="button button--ghost" disabled={busy} onClick={() => void downloadSchedule("xlsx")}>
            <FileSpreadsheet /> Excel
          </button>
          <button className="button button--ghost" disabled={busy} onClick={() => void downloadSchedule("pdf")}>
            <FileDown /> PDF
          </button>
        </div>
      </section>

      {error && <div className="form-error">{error}</div>}

      {!canManage && (
        <div className="info-banner">
          Smjene su dostupne samo za pregled. Za ručni upis potrebna je ovlast
          „Upravljanje smjenama”.
        </div>
      )}

      {canManage && activeTypes.length === 0 && (
        <div className="info-banner">
          Nema aktivne vrste smjene. Otvorite Šifrarnik smjena i aktivirajte
          postojeću ili dodajte novu vrstu prije upisa rasporeda.
        </div>
      )}

      {!isMobile && (
        <section className="shift-grid-card">
          <div className="shift-grid">
            <div className="shift-grid__corner">Zaposlenik</div>
            {dates.map((date) => {
              const holiday = holidayFor(date);
              return (
                <div className={`shift-grid__day ${holiday ? "is-holiday" : ""}`} key={isoDate(date)} title={holiday?.name}>
                  <strong>{date.toLocaleDateString("hr-HR", { weekday: "short" })}</strong>
                  <small>{date.toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit" })}</small>
                  {holiday && <em>{holiday.name}</em>}
                </div>
              );
            })}
            {users.map((user) => (
              <div className="shift-grid__row" key={user.id}>
                <div className="shift-user">
                  <span className="avatar avatar--soft">{user.firstName[0]}{user.lastName[0]}</span>
                  <strong>{user.firstName} {user.lastName}</strong>
                </div>
                {dates.map((date) => {
                  const key = isoDate(date);
                  const current = items.find((item) => item.userId === user.id && item.shiftDate.slice(0, 10) === key);
                  const holiday = holidayFor(date);
                  return (
                    <button
                      type="button"
                      key={key}
                      className={`shift-cell ${current ? "has-shift" : ""} ${holiday ? "is-holiday" : ""}`}
                      onClick={() => openCell(user.id, key)}
                      title={holiday?.name}
                      style={current ? { borderColor: current.color, background: `${current.color}12` } : undefined}
                    >
                      {current ? (
                        <>
                          <strong style={{ color: current.color }}>{current.code}</strong>
                          <small>{current.startsAt ? `${formatTime(current.startsAt)}–${formatTime(current.endsAt)}` : current.typeName}</small>
                        </>
                      ) : canManage ? <Plus /> : <span>—</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="shift-mobile-view">
        <div className="shift-mobile-days" aria-label="Odaberi dan">
          {dates.map((date) => {
            const key = isoDate(date);
            const holiday = holidayFor(date);
            return (
              <button type="button" className={`${mobileDate === key ? "is-active" : ""} ${holiday ? "is-holiday" : ""}`} key={key} onClick={() => setMobileDate(key)}>
                <small>{date.toLocaleDateString("hr-HR", { weekday: "short" })}</small>
                <strong>{date.getDate()}</strong>
                {holiday && <span />}
              </button>
            );
          })}
        </div>
        {holidayFor(new Date(`${mobileDate}T12:00:00`)) && (
          <div className="shift-mobile-holiday">
            <CalendarDays />
            <span><small>Neradni dan</small><strong>{holidayFor(new Date(`${mobileDate}T12:00:00`))!.name}</strong></span>
          </div>
        )}
        <MobileVirtualList
          className="shift-mobile-list"
          items={users}
          estimateSize={92}
          getKey={(user) => user.id}
          empty={<div className="table-empty">Nema zaposlenika u ovom prikazu.</div>}
          renderItem={(user) => {
            const current = items.find((item) => item.userId === user.id && item.shiftDate.slice(0, 10) === mobileDate);
            return (
              <button type="button" className={`shift-mobile-card mobile-native-card${current ? " has-shift" : ""}`} onClick={() => openCell(user.id, mobileDate)} disabled={!canManage}>
                <span className="avatar avatar--soft">{user.firstName[0]}{user.lastName[0]}</span>
                <span><strong>{user.firstName} {user.lastName}</strong><small>{current ? current.note || current.typeName : "Smjena nije upisana"}</small></span>
                {current ? (
                  <span className="shift-mobile-card__shift" style={{ color: current.color, background: `${current.color}14` }}>
                    <strong>{current.code}</strong>
                    <small>{current.startsAt ? `${formatTime(current.startsAt)}–${formatTime(current.endsAt)}` : current.typeName}</small>
                  </span>
                ) : <span className="shift-mobile-card__empty">{canManage ? <Plus /> : "—"}</span>}
              </button>
            );
          }}
        />
      </section>

      {edit && (
        <div className="modal-backdrop">
          <form className="mini-modal" onSubmit={saveShift}>
            <header>
              <div><span className="eyebrow">{formatDate(edit.date)}</span><h2>{edit.shift ? "Uredi smjenu" : "Dodaj smjenu"}</h2></div>
              <button type="button" className="icon-button" onClick={() => setEdit(null)}><X /></button>
            </header>
            {holidays.find((holiday) => holiday.date === edit.date) && (
              <div className="shift-holiday-note">
                <CalendarDays />
                <span><strong>{holidays.find((holiday) => holiday.date === edit.date)!.name}</strong><small>Smjena se i dalje može evidentirati zbog dežurstva.</small></span>
              </div>
            )}
            <label className="field">
              <span>Vrsta smjene</span>
              <select required value={typeId} onChange={(event) => setTypeId(event.target.value)}>
                <option value="" disabled>Odaberite vrstu smjene</option>
                {activeTypes.map((type) => <option key={type.id} value={type.id}>{type.code} · {type.name}</option>)}
              </select>
            </label>
            <label className="field"><span>Napomena</span><textarea value={note} onChange={(event) => setNote(event.target.value)} /></label>
            {manualError && <div className="form-error">{manualError}</div>}
            <footer>
              {edit.shift && <button type="button" className="button button--danger" disabled={busy} onClick={() => void deleteShift()}>Obriši</button>}
              <button className="button button--primary" disabled={busy || !typeId}>Spremi</button>
            </footer>
          </form>
        </div>
      )}

      {typesModal && (
        <div className="modal-backdrop">
          <section className="operational-modal shift-catalog-modal">
            <header>
              <div><span className="eyebrow">ŠIFRARNIK FIRME</span><h2>Vrste smjena</h2></div>
              <button className="icon-button" onClick={() => setTypesModal(false)}><X /></button>
            </header>
            <div className="modal-scroll shift-catalog-layout">
              <section className="shift-type-list">
                <header><span><strong>Aktivne i arhivirane vrste</strong><small>Svaka firma može prilagoditi zadane smjene i dodati vlastite.</small></span><button type="button" className="button button--ghost button--small" onClick={resetTypeForm}><Plus /> Nova</button></header>
                <div>
                  {options.types.map((type) => (
                    <article key={type.id} className={`${editingTypeId === type.id ? "is-active" : ""} ${type.status === "INACTIVE" ? "is-inactive" : ""}`} onClick={() => editType(type)}>
                      <i style={{ background: type.color }} />
                      <span><strong>{type.code} · {type.name}</strong><small>{type.isWorking ? `${formatTime(type.startsAt)}–${formatTime(type.endsAt)} · pauza ${type.breakMinutes} min` : "Neradni / slobodni dan"}</small></span>
                      <em>{type.status === "ACTIVE" ? "Aktivna" : "Arhivirana"}</em>
                      <Pencil />
                    </article>
                  ))}
                </div>
              </section>
              <form className="shift-type-form" onSubmit={saveType}>
                <header><strong>{editingTypeId ? "Uredi vrstu smjene" : "Nova vrsta smjene"}</strong><small>Primjer: M11 · Međusmjena · 11:00–17:00</small></header>
                <div className="form-grid">
                  <label className="field"><span>Šifra</span><input required maxLength={20} value={typeForm.code} onChange={(event) => setTypeForm({ ...typeForm, code: event.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="M11" /></label>
                  <label className="field"><span>Naziv</span><input required value={typeForm.name} onChange={(event) => setTypeForm({ ...typeForm, name: event.target.value })} placeholder="Međusmjena" /></label>
                  <label className="field"><span>Početak</span><input type="time" value={typeForm.startsAt} disabled={!typeForm.isWorking} onChange={(event) => setTypeForm({ ...typeForm, startsAt: event.target.value })} /></label>
                  <label className="field"><span>Završetak</span><input type="time" value={typeForm.endsAt} disabled={!typeForm.isWorking} onChange={(event) => setTypeForm({ ...typeForm, endsAt: event.target.value })} /></label>
                  <label className="field"><span>Pauza (minute)</span><input type="number" min={0} max={600} value={typeForm.breakMinutes} disabled={!typeForm.isWorking} onChange={(event) => setTypeForm({ ...typeForm, breakMinutes: Number(event.target.value) })} /></label>
                  <label className="field"><span>Boja u rasporedu</span><span className="shift-color-input"><input type="color" value={typeForm.color} onChange={(event) => setTypeForm({ ...typeForm, color: event.target.value })} /><b style={{ color: typeForm.color }}>{typeForm.color.toUpperCase()}</b></span></label>
                </div>
                <label className="checkbox-field"><input type="checkbox" checked={typeForm.isWorking} onChange={(event) => setTypeForm({ ...typeForm, isWorking: event.target.checked, startsAt: event.target.checked ? typeForm.startsAt || "06:00" : "", endsAt: event.target.checked ? typeForm.endsAt || "14:00" : "", breakMinutes: event.target.checked ? typeForm.breakMinutes : 0 })} /><span><strong>Radna smjena</strong><small>Isključite za slobodan ili neradni dan.</small></span></label>
                {editingTypeId && options.types.find((item) => item.id === editingTypeId)?.status === "INACTIVE" && <label className="checkbox-field"><input type="checkbox" checked={typeForm.status === "ACTIVE"} onChange={(event) => setTypeForm({ ...typeForm, status: event.target.checked ? "ACTIVE" : "INACTIVE" })} /><span><strong>Ponovno aktiviraj smjenu</strong><small>Nakon spremanja ponovno će biti dostupna u rasporedu i predlošcima.</small></span></label>}
                <footer>
                  {editingTypeId && typeForm.status === "ACTIVE" && <button type="button" className="button button--danger" onClick={() => { const type = options.types.find((item) => item.id === editingTypeId); if (type) void archiveType(type); }}><Trash2 /> Arhiviraj</button>}
                  <span />
                  <button className="button button--primary" disabled={busy}>{editingTypeId ? "Spremi izmjene" : "Dodaj smjenu"}</button>
                </footer>
              </form>
            </div>
          </section>
        </div>
      )}

      {templateModal && (
        <div className="modal-backdrop">
          <form className="operational-modal shift-template-modal" onSubmit={saveTemplate}>
            <header>
              <div><span className="eyebrow">TJEDNI UZORAK</span><h2>{editingTemplateId ? "Uredi predložak" : "Novi predložak smjena"}</h2></div>
              <button type="button" className="icon-button" onClick={() => setTemplateModal(false)}><X /></button>
            </header>
            <div className="modal-scroll">
              {options.templates.length > 0 && (
                <section className="shift-template-library">
                  <header><span><strong>Spremljeni predlošci</strong><small>Kliknite predložak za uređivanje ili izradite novi.</small></span><button type="button" className="button button--ghost button--small" onClick={resetTemplateForm}><Plus /> Novi</button></header>
                  <div>{options.templates.map((template) => <article key={template.id} className={editingTemplateId === template.id ? "is-active" : ""}><button type="button" onClick={() => void editTemplate(template.id)}><LayoutTemplate /><span><strong>{template.name}</strong><small>{template.cycleWeeks === 1 ? "Tjedni uzorak" : `Ciklus od ${template.cycleWeeks} tjedna`}</small></span></button><button type="button" className="icon-button icon-button--danger" onClick={() => void archiveTemplate(template)} title="Arhiviraj"><Trash2 /></button></article>)}</div>
                </section>
              )}
              <div className="form-grid">
                <label className="field"><span>Naziv</span><input required value={templateForm.name} onChange={(event) => setTemplateForm({ ...templateForm, name: event.target.value })} placeholder="Jutarnja ekipa" /></label>
                <label className="field"><span>Broj tjedana u ciklusu</span><input type="number" min={1} max={6} value={templateForm.cycleWeeks} onChange={(event) => setTemplateForm({ ...templateForm, cycleWeeks: Math.max(1, Math.min(6, Number(event.target.value))) })} /></label>
                <label className="field field--full"><span>Opis</span><input value={templateForm.description} onChange={(event) => setTemplateForm({ ...templateForm, description: event.target.value })} placeholder="Kome i kada se ovaj uzorak koristi" /></label>
              </div>
              <div className="info-banner">Svaki se tjedan ponavlja po istim danima. Prazan dan ostaje prazan — petak se više nikada neće pomaknuti na subotu u sljedećem ciklusu.</div>
              <div className="shift-template-weeks">
                {Array.from({ length: templateForm.cycleWeeks }, (_, weekIndex) => (
                  <section key={weekIndex}>
                    <header><strong>{templateForm.cycleWeeks === 1 ? "Tjedni raspored" : `Tjedan ${weekIndex + 1}`}</strong><small>Odaberite smjenu samo za dane koji se trebaju prepisivati.</small></header>
                    <div>{dayNames.map((dayName, dayIndex) => { const dayOffset = weekIndex * 7 + dayIndex; const entry = templateForm.entries[dayOffset]!; return <label className={`field ${dayIndex > 4 ? "is-weekend" : ""}`} key={dayOffset}><span>{dayName}</span><select value={entry.shiftTypeId} onChange={(event) => setTemplateForm({ ...templateForm, entries: templateForm.entries.map((item) => item.dayOffset === dayOffset ? { ...item, shiftTypeId: event.target.value } : item) })}><option value="">Bez smjene</option>{activeTypes.map((type) => <option key={type.id} value={type.id}>{type.code} · {type.name}</option>)}</select></label>; })}</div>
                  </section>
                ))}
              </div>
            </div>
            <footer><button type="button" className="button button--ghost" onClick={() => setTemplateModal(false)}>Odustani</button><button className="button button--primary" disabled={busy}>{editingTemplateId ? "Spremi predložak" : "Dodaj predložak"}</button></footer>
          </form>
        </div>
      )}

      {applyModal && (
        <div className="modal-backdrop">
          <form className="operational-modal shift-apply-modal" onSubmit={applyTemplate}>
            <header><div><span className="eyebrow">SIGURNA PRIMJENA</span><h2>Primijeni predložak</h2></div><button type="button" className="icon-button" onClick={() => setApplyModal(false)}><X /></button></header>
            <div className="modal-scroll">
              <div className="form-grid">
                <label className="field"><span>Predložak</span><select required value={applyForm.templateId} onChange={(event) => setApplyForm({ ...applyForm, templateId: event.target.value, userIds: [] })}><option value="">Odaberite predložak</option>{options.templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}</select></label>
                <label className="field"><span>Početak prvog tjedna</span><CroatianDateInput required value={applyForm.startsOn} onValueChange={(value) => setApplyForm({ ...applyForm, startsOn: mondayIso(value), userIds: [] })} ariaLabel="Početni ponedjeljak" /><small>Datum se automatski poravnava na ponedjeljak.</small></label>
                <label className="field"><span>Broj ponavljanja ciklusa</span><input type="number" min={1} max={52} value={applyForm.cycles} onChange={(event) => setApplyForm({ ...applyForm, cycles: Number(event.target.value), userIds: [] })} /></label>
                <div className="shift-target-summary"><CalendarDays /><span><small>Ciljno razdoblje</small><strong>{applyPreview ? `${formatDate(applyPreview.targetFrom)} – ${formatDate(applyPreview.targetTo)}` : "Provjeravam…"}</strong></span></div>
              </div>
              {previewLoading && <div className="shift-preview-loading">Provjeravam postojeće rasporede…</div>}
              {applyPreview && <AvailabilityPicker users={applyPreview.users} selectedIds={applyForm.userIds} onChange={(userIds) => setApplyForm({ ...applyForm, userIds })} emptyText="Nema zaposlenika za odabrani prikaz." />}
            </div>
            <footer><button type="button" className="button button--ghost" onClick={() => setApplyModal(false)}>Odustani</button><button className="button button--primary" disabled={busy || previewLoading || !applyForm.templateId || !applyForm.userIds.length}>Primijeni na {applyForm.userIds.length || 0} zaposlenika</button></footer>
          </form>
        </div>
      )}

      {copyModal && (
        <div className="modal-backdrop">
          <form className="operational-modal shift-apply-modal" onSubmit={copyWeek}>
            <header><div><span className="eyebrow">KOPIRANJE PO DANIMA</span><h2>Kopiraj prikazani tjedan</h2></div><button type="button" className="icon-button" onClick={() => setCopyModal(false)}><X /></button></header>
            <div className="modal-scroll">
              <div className="shift-copy-summary">
                <article><small>Izvorni tjedan</small><strong>{formatDate(isoDate(week))} – {formatDate(isoDate(addDays(week, 6)))}</strong></article>
                <ChevronRight />
                <label className="field"><span>Početak ciljnog tjedna</span><CroatianDateInput required value={copyForm.targetFrom} onValueChange={(value) => setCopyForm({ targetFrom: mondayIso(value), userIds: [] })} ariaLabel="Početak ciljnog tjedna" /><small>Uvijek se kopira ponedjeljak na ponedjeljak, utorak na utorak itd.</small></label>
              </div>
              {previewLoading && <div className="shift-preview-loading">Provjeravam ciljni tjedan…</div>}
              {copyPreview && <AvailabilityPicker users={copyPreview.users} selectedIds={copyForm.userIds} onChange={(userIds) => setCopyForm({ ...copyForm, userIds })} emptyText="Nema zaposlenika za kopiranje." />}
            </div>
            <footer><button type="button" className="button button--ghost" onClick={() => setCopyModal(false)}>Odustani</button><button className="button button--primary" disabled={busy || previewLoading || !copyForm.userIds.length}>Kopiraj za {copyForm.userIds.length || 0} zaposlenika</button></footer>
          </form>
        </div>
      )}

      {holidaysModal && (
        <div className="modal-backdrop">
          <section className="operational-modal shift-holidays-modal">
            <header><div><span className="eyebrow">KALENDAR RADA</span><h2>Praznici i blagdani</h2></div><button className="icon-button" onClick={() => setHolidaysModal(false)}><X /></button></header>
            <div className="modal-scroll">
              <div className="shift-holidays-heading">
                <span><strong>Neradni dani</strong><small>Državni blagdani dodani su automatski. Interni dani vrijede i za obračun godišnjeg.</small></span>
                <select value={holidayYear} onChange={(event) => void loadHolidayYear(Number(event.target.value))}>{[holidayYear - 1, holidayYear, holidayYear + 1].map((year) => <option key={year} value={year}>{year}</option>)}</select>
              </div>
              <form className="leave-holiday-form" onSubmit={addHoliday}>
                <label className="field"><span>Datum</span><CroatianDateInput required value={holidayForm.date} onValueChange={(value) => setHolidayForm({ ...holidayForm, date: value })} ariaLabel="Datum neradnog dana" /></label>
                <label className="field"><span>Naziv</span><input required minLength={2} value={holidayForm.name} onChange={(event) => setHolidayForm({ ...holidayForm, name: event.target.value })} placeholder="Kolektivni godišnji…" /></label>
                <button className="button button--primary button--small" disabled={busy}><Plus /> Dodaj</button>
              </form>
              <div className="leave-holiday-list">{yearHolidays.map((holiday) => <article key={`${holiday.source}-${holiday.date}`}><time>{formatDate(holiday.date)}</time><span><strong>{holiday.name}</strong><small>{holiday.source === "STATUTORY" ? "Državni blagdan" : "Interni neradni dan"}</small></span>{holiday.source === "COMPANY" && holiday.id && <button className="icon-button icon-button--danger" onClick={() => void removeHoliday(holiday.id!)}><Trash2 /></button>}</article>)}</div>
            </div>
            <footer><span /><button className="button button--primary" onClick={() => setHolidaysModal(false)}>Gotovo</button></footer>
          </section>
        </div>
      )}
    </div>
  );
}
